import { createContext, type PropsWithChildren, useContext } from 'react';

import { useLayerManagerFactory } from './factory';

export type LayerId = string;

export interface LayerManager {
  activeLayers: Set<LayerId>;
  enterLayer: (id: LayerId) => void;
  exitLayer: (id: LayerId) => void;
}

class NoLayerManagerContextProvided extends Error {
  constructor() {
    super(
      'No `LayerManagerContext` was provided. Make sure to provide one using e.g. `LayersProvider` with optional manager instance created using `useLayerManagerFactory`.',
    );
  }
}

const noopLayerManagerContext: LayerManager = new Proxy<LayerManager>({} as never, {
  get() {
    throw new NoLayerManagerContextProvided();
  },
});

export const LayerManagerContext = createContext<LayerManager>(noopLayerManagerContext);

export function useLayerManager() {
  return useContext(LayerManagerContext);
}

type LayersProviderProps = PropsWithChildren<{
  manager?: LayerManager;
}>;

export function LayersProvider({ children, manager: providedManager }: LayersProviderProps) {
  const fallbackManager = useLayerManagerFactory();
  const manager = providedManager || fallbackManager;

  return <LayerManagerContext.Provider value={manager}>{children}</LayerManagerContext.Provider>;
}
